/**
 * One line app-wide. The snapshot drives every surface (the Dock's ball, the
 * type-mode mark); the refs carry per-frame audio energy so nothing
 * re-renders sixty times a second.
 */
import { create } from "zustand";
import type {
  ComposerPanel,
  LineMicStats,
  LineMutePayload,
  RouterAction,
  RouterEvent,
} from "@bash/shared";
import { EVENTS } from "@/constants/events";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import { useConnectionStore } from "@/stores/connectionStore";
import {
  INITIAL_LINE,
  reduceLine,
  type LineInput,
  type LinePhase,
  type LineSnapshot,
} from "./lineMachine";
import type { LineHandle } from "./liveClient";

/**
 * Tells the server which it is. The mute takes the mic off the air HERE
 * (track.enabled = false), and the vendor sends no input transcript either way,
 * so without this the server's idle policy reads a muted line as an empty room:
 * it asks "are you still there?" into a dead microphone and closes the line a
 * grace later. Sent on every flip and once when the line opens.
 *
 * The flag and nothing else: WHICH line is the server's to know, off the socket
 * it authenticated. A socket that is down is told nothing, and the next line's
 * own open frame states it afresh.
 *
 * The silent mic rides the same rail (2026-09-21): a microphone that delivers
 * nothing is the other thing the server cannot see. `silent` is on the frame
 * only when the watchdog has a word to say, so a healthy line's frame is the
 * flag alone, as it always was. So does the one-shot evidence line (`stats`,
 * the open line's onMicStats): on exactly one frame a call.
 */
export function tellLineMuted(
  muted: boolean,
  silent?: boolean,
  stats?: LineMicStats
): void {
  const socket = useConnectionStore.getState().getSocket();
  if (!socket?.connected) return;
  const payload: LineMutePayload = {
    muted,
    ...(silent === undefined ? {} : { silent }),
    ...(stats ? { stats } : {}),
  };
  socket.emit(EVENTS.COMPOSER_LINE_MUTE, payload);
}

/** The type door: "ball" is the composer at rest, "type" the typed side door. */
export type LineDoor = "ball" | "type";

/** The two panel kinds that open a door rather than draw a card. */
export type LineDoorPanel = "history" | "tray";

export interface LiveStoreState {
  line: LineSnapshot;
  /** Router events of the current spoken turn, for the receipt card. Reset on each delegation. */
  turn: RouterEvent[];
  /**
   * The delegation whose events fill `turn`, stamped by the socket handler
   * when a new delegation resets it. A dismiss (resetTurn) empties the turn
   * but keeps the id, so a late failure of the same delegation can still find
   * its receipt; only the next delegation replaces it.
   */
  turnDelegationId: string | null;
  /**
   * The open line's media handle, owned by useLine. In the store (not a
   * module variable) so a surface rendering on the transition that set it
   * sees it at once, without waiting for the next tick.
   */
  handle: LineHandle | null;
  /**
   * The mic, off the air (the founder, 2026-09-19: "please add it back now").
   * One level beside the handle, so the tray's button and the track it takes
   * off the air can never disagree and a surface remounting mid-call reads
   * the truth. A fresh line always starts unmuted (useLine).
   */
  muted: boolean;
  setMuted(muted: boolean): void;
  /**
   * The silent mic (2026-09-21): the watchdog found no input with signal
   * (lib/voice/mic-watch.ts, through the open line's onMicSilent). One level
   * beside the mute, for the stage's one quiet line; null on a healthy line,
   * and reset on the same beats that reset the mute (useLine).
   *
   * "deaf" is the heal's own word (R-D1): a call the vendor never heard, redialled once, and
   * deaf again. It shares this one slot because the stage may only ever say one of these.
   */
  micTrouble: null | "silent" | "deaf";
  setMicTrouble(trouble: null | "silent" | "deaf"): void;
  /**
   * THE DEAF CALL, the server's half (R-D1, off the state frame). `awaitingVoice`: its greeting
   * is finished and it is waiting to hear him. `heard`: it has had an input transcript or a
   * delegation on this call. Both false on a line that has said nothing about them, which is
   * what an older server and a flag-off line both look like — and neither ever dials.
   */
  awaitingVoice: boolean;
  heard: boolean;
  /**
   * ATTACH ON THE CALL (2026-09-24): how many files wait on the line for the next ask, the
   * server's own count off the state frame (useLineAttach clears its chips when it falls to 0).
   * 0 on a line that has said nothing about it, and reset with the mute on every open and close.
   */
  staged: number;
  /**
   * Bumped by every state frame that carries `staged`, so a reader can tell a frame from any other
   * write to the store: the count is a level, and a 0 frame clears the chips even when the frame
   * that raised it was lost (a socket reconnect).
   */
  stagedFrames: number;
  /**
   * THE LINE STAYS ON (T8b): the server's closing frame named `rollover` (the Mac's standby, at the
   * cap), so when this line goes off the page dials again at once (useLine). Raised by the socket
   * handler, spent by the dial, and dropped by his own close: his hang-up is never rolled over.
   */
  rollingOver: boolean;
  /**
   * The type door, one app-wide (founder 09-11: "show the existing composer"):
   * the box (BashPill) hosted by the Dock is the door on every route, and
   * either surface can open or close it. Independent of the line: the
   * door can be open with the line off.
   */
  door: LineDoor;
  /**
   * The box's ⌃⌃ history, one app-wide like the door (spec 2026-09-12
   * one-composer): the Dock hears the pair (one clock with the single tap's
   * line toggle) and the box draws the card, so both read it here. The stamp
   * rides the open gesture so render stays pure. A level, not an edge: it
   * lives only while the door is open, so closeDoor drops it (the Dock's
   * hidden routes are the other half, Dock.tsx).
   */
  historyOpen: boolean;
  historyOpenedAt: number | null;
  /**
   * The door standing open because the LINE opened it, for a spoken history
   * or tray (linePanels.ts). A spoken "close it" takes down what the line put
   * up and no more, so this is the one thing that tells the box the line
   * opened from the box they opened themselves; the call ending takes the
   * line's door with it, the way it takes the crown's panel.
   */
  doorOpenedByLine: boolean;
  /**
   * The Island (the tray), one level app-wide (composer panels, spec
   * 2026-09-19 §3.4): the Dock hosts it and the ⌥ tap toggles it there; a
   * tray panel from the pill or the Line opens it here. Independent of the
   * door and of the history.
   */
  trayOpen: boolean;
  setTrayOpen(open: boolean): void;
  /**
   * A spoken app_control or open_url of the person's own line, parked by the
   * socket handler for LineActions (mounted by AppShell on every app route,
   * the task page and support included, where there is no dock) to perform
   * and clear (spec 2026-09-17 §5.4). Delegated turns only: a typed turn
   * already navigates through the SSE lane. The machine stays pure.
   */
  pendingAction: RouterAction | null;
  /**
   * A spoken `history` or `tray` heard where the Dock paints nothing (/task,
   * /support): the door has no host there, so it waits here the way
   * pendingAction waits, and LineActions rides to /home and opens it once a
   * dock route is on screen (R1). Null everywhere the door opened at once.
   */
  pendingDoor: LineDoorPanel | null;
  setPendingDoor(door: LineDoorPanel | null): void;
  /**
   * The crown's one panel (composer panels, spec 2026-09-19 §3.4): a sheet or a
   * receipt a SPOKEN turn put up, held here rather than in `turn` because the
   * next delegation empties the turn and a panel has no timer (only × / Escape
   * / "close it" take it down). The socket handler writes it (linePanels.ts).
   */
  panel: ComposerPanel | null;
  setPanel(panel: ComposerPanel | null): void;
  dispatch(i: LineInput): void;
  pushTurnEvent(e: RouterEvent): void;
  resetTurn(): void;
  /** Opens the door. A seed (the letter that opened it) is appended to the
   *  home composer's parked draft, never over parked words. */
  openDoor(seed?: string): void;
  closeDoor(): void;
  toggleHistory(): void;
  closeHistory(): void;
}

/** The phases a call is over in: the goodbye is on its way ("closing") or it
 *  already landed ("off", where a failure lands too). */
const ENDING: ReadonlySet<LinePhase> = new Set<LinePhase>(["closing", "off"]);

export const useLiveStore = create<LiveStoreState>((set, get) => ({
  line: INITIAL_LINE,
  turn: [],
  turnDelegationId: null,
  handle: null,
  muted: false,
  // One call, not two: the level and the track move together, so no surface
  // can flip the button without taking the mic off the air.
  setMuted: (muted) => {
    const handle = get().handle;
    handle?.setMuted(muted);
    set({ muted });
    // The server cannot hear a muted mic as anything but silence, so it is told.
    if (handle) tellLineMuted(muted);
  },
  micTrouble: null,
  awaitingVoice: false,
  heard: false,
  staged: 0,
  stagedFrames: 0,
  rollingOver: false,
  // Told once per change, with the mute as it stands: the frame's spine is
  // the mute, and this word must never move it. Only the watchdog's "silent"
  // is the server's business; "deaf" is the stage's own word about a line the
  // server already knows it never heard.
  setMicTrouble: (micTrouble) => {
    if (get().micTrouble === micTrouble) return;
    set({ micTrouble });
    if (get().handle) tellLineMuted(get().muted, micTrouble === "silent");
  },
  door: "ball",
  historyOpen: false,
  historyOpenedAt: null,
  doorOpenedByLine: false,
  trayOpen: false,
  // The same level twice is a no-op, not a notification: the Dock closes the
  // Island on EVERY pointerdown outside it, and a plain set would wake every
  // subscriber of this store on every click.
  setTrayOpen: (open) =>
    set((s) => (s.trayOpen === open ? s : { trayOpen: open })),
  pendingAction: null,
  pendingDoor: null,
  setPendingDoor: (pendingDoor) => set({ pendingDoor }),
  panel: null,
  setPanel: (panel) => set({ panel }),
  dispatch: (i) => {
    const s = get();
    const before = s.line;
    const line = reduceLine(before, i);
    // The crown's panel belongs to the call that put it up (R-24): when the
    // line ends — the hang-up, the server's own close, a failure — the sheet
    // comes down with it, or it sits on the crown after the call and on the
    // next dock route. The transition, not the level: a new delegation inside
    // the same call never clears it.
    //
    // A door parked for a route with no Dock belongs to that call just as much:
    // it is still riding to /home when the person hangs up, or the ride is cut
    // short by a navigation of their own, and nothing consumes it. Left here it
    // would open their history or the Island unasked on some later visit to a
    // dock route, long after the call that spoke for it ended.
    const ending = ENDING.has(line.phase) && !ENDING.has(before.phase);
    if (!ending) return set({ line });
    // The box the LINE opened rides that same rail: it came up for a spoken
    // history or tray, so it goes down with the call that asked for it and
    // the ball is back at rest — their hang-up, the spoken one the peer
    // closes, a failure, all one beat. A box they opened by hand is theirs.
    const gone = {
      line,
      panel: null,
      pendingDoor: null,
      doorOpenedByLine: false,
    };
    set(
      s.doorOpenedByLine
        ? { ...gone, door: "ball", historyOpen: false, historyOpenedAt: null }
        : gone
    );
  },
  pushTurnEvent: (e) => set({ turn: [...get().turn, e] }),
  resetTurn: () => set({ turn: [] }),
  openDoor: (seed) => {
    if (seed) {
      const drafts = useComposerDraftStore.getState();
      drafts.parkHomeDraft(drafts.homeDraft + seed);
    }
    set({ door: "type" });
  },
  closeDoor: () =>
    set({
      door: "ball",
      historyOpen: false,
      historyOpenedAt: null,
      doorOpenedByLine: false,
    }),
  toggleHistory: () =>
    set((s) => ({
      historyOpen: !s.historyOpen,
      historyOpenedAt: s.historyOpen ? s.historyOpenedAt : Date.now(),
    })),
  closeHistory: () => set({ historyOpen: false }),
}));

/** 0..1 mic energy, written by the talk driver each frame. */
export const liveEnergyRef = { current: 0 };
/** 0..1 remote energy: Bash's voice. */
export const liveBashRef = { current: 0 };
/** The mouth band, 0 (shut) to 3 (wide), from the remote analyser. */
export const liveTalkRef: { current: 0 | 1 | 2 | 3 } = { current: 0 };
